/**
 * Module-level cache: userId string → { label, image?, zoomStatus? }.
 *
 * Populated by `useInterviewerField.loadOptions` whenever the async
 * select fetches interviewers.  Read by `useEditConfirmation.requestSave`
 * so newly searched+selected interviewers (whose metadata is NOT in the
 * initial `participant_default_options`) still appear in the confirmation
 * dialog with correct name/avatar.
 *
 * Zoom hosting fields are cached alongside so the Host field's
 * Paid/Busy/Needs-license tags + disabled-option logic can resolve a
 * picked interviewer's status without a refetch (sch-28 host parity).
 */

export interface CachedInterviewerZoomStatus {
  can_host_zoom_meeting?: boolean;
  zoom_rate_limit_reached?: boolean;
  zoom_plan?: number;
  zoom_plan_type?: string;
}

interface CachedOption {
  label: string;
  image?: string;
  zoomStatus?: CachedInterviewerZoomStatus;
}

const cache = new Map<string, CachedOption>();

export function cacheInterviewerOptions(
  options: Array<{
    value: string;
    label: string;
    image?: string;
    zoomStatus?: CachedInterviewerZoomStatus;
  }>
): void {
  for (const o of options) {
    if (!o.value) continue;
    cache.set(o.value, {
      label: o.label,
      image: o.image,
      zoomStatus: o.zoomStatus,
    });
  }
}

export function getCachedInterviewerOption(
  id: string
): CachedOption | undefined {
  return cache.get(id);
}
